import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, FlatList } from 'react-native';
import { Card, Chip, ActivityIndicator, Divider } from 'react-native-paper';
import api from '../../services/api';

const PERIODOS_RAPIDOS = [
  { id: 'hoy', label: 'Hoy' },
  { id: 'esta_semana', label: 'Esta Semana' },
  { id: 'este_mes', label: 'Este Mes' },
  { id: 'mes_pasado', label: 'Mes Pasado' },
  { id: 'este_anio', label: 'Este Año' },
];

const ComisionReporteScreen = () => {
  const [reporte, setReporte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriodo, setSelectedPeriodo] = useState('este_mes');

  useEffect(() => {
    loadReporte();
  }, [selectedPeriodo]);

  const getFechas = (periodo) => {
    const now = new Date();
    let fecha_inicio, fecha_fin;

    switch (periodo) {
      case 'hoy':
        fecha_inicio = fecha_fin = now.toISOString().split('T')[0];
        break;
      case 'esta_semana': {
        const day = now.getDay();
        const start = new Date(now);
        start.setDate(now.getDate() - (day === 0 ? 6 : day - 1));
        fecha_inicio = start.toISOString().split('T')[0];
        fecha_fin = now.toISOString().split('T')[0];
        break;
      }
      case 'este_mes':
        fecha_inicio = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
        fecha_fin = now.toISOString().split('T')[0];
        break;
      case 'mes_pasado': {
        const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
        fecha_inicio = prevMonth.toISOString().split('T')[0];
        fecha_fin = lastDay.toISOString().split('T')[0];
        break;
      }
      case 'este_anio':
        fecha_inicio = `${now.getFullYear()}-01-01`;
        fecha_fin = now.toISOString().split('T')[0];
        break;
      default:
        fecha_inicio = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
        fecha_fin = now.toISOString().split('T')[0];
    }
    return { fecha_inicio, fecha_fin };
  };

  const loadReporte = async () => {
    try {
      setLoading(true);
      const { fecha_inicio, fecha_fin } = getFechas(selectedPeriodo);
      const data = await api.getComisionesReporte({ fecha_inicio, fecha_fin });
      setReporte(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalGeneral = reporte.reduce((sum, r) => sum + parseFloat(r.total_general || 0), 0);
  const totalPendiente = reporte.reduce((sum, r) => sum + parseFloat(r.total_pendiente || 0), 0);
  const totalPagada = reporte.reduce((sum, r) => sum + parseFloat(r.total_pagada || 0), 0);

  return (
    <ScrollView style={styles.container}>
      {/* Filtros de período */}
      <View style={styles.filtersContainer}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {PERIODOS_RAPIDOS.map((periodo) => (
            <Chip
              key={periodo.id}
              onPress={() => setSelectedPeriodo(periodo.id)}
              style={[styles.chip, selectedPeriodo === periodo.id && styles.chipActive]}
              textStyle={selectedPeriodo === periodo.id ? styles.chipTextActive : styles.chipTextInactive}
            >
              {periodo.label}
            </Chip>
          ))}
        </ScrollView>
      </View>

      {/* Resumen general */}
      <Card style={styles.card}>
        <Card.Title title="Resumen General" />
        <Card.Content>
          <View style={styles.resumenGrid}>
            <View style={styles.resumenItem}>
              <Text style={[styles.resumenValue, { color: '#333' }]}>L. {totalGeneral.toFixed(2)}</Text>
              <Text style={styles.resumenLabel}>Total Comisiones</Text>
            </View>
            <View style={styles.resumenItem}>
              <Text style={[styles.resumenValue, { color: '#FFC107' }]}>L. {totalPendiente.toFixed(2)}</Text>
              <Text style={styles.resumenLabel}>Pendiente</Text>
            </View>
            <View style={styles.resumenItem}>
              <Text style={[styles.resumenValue, { color: '#28A745' }]}>L. {totalPagada.toFixed(2)}</Text>
              <Text style={styles.resumenLabel}>Pagada</Text>
            </View>
          </View>
        </Card.Content>
      </Card>

      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#007bff" />
        </View>
      ) : (
        <>
          {reporte.map((item, index) => (
            <Card key={item.vendedor_id} style={styles.card}>
              <Card.Content>
                <View style={styles.vendedorHeader}>
                  <Text style={styles.vendedorNombre}>{item.vendedor_nombre}</Text>
                  <Text style={styles.vendedorTotal}>L. {parseFloat(item.total_general || 0).toFixed(2)}</Text>
                </View>
                <Divider style={styles.divider} />
                <View style={styles.detalleRow}>
                  <Text style={styles.detalleLabel}>Comisiones:</Text>
                  <Text style={styles.detalleValue}>{item.total_comisiones}</Text>
                </View>
                <View style={styles.detalleRow}>
                  <Text style={styles.detalleLabel}>Pendiente:</Text>
                  <Text style={[styles.detalleValue, { color: '#FFC107' }]}>L. {parseFloat(item.total_pendiente || 0).toFixed(2)}</Text>
                </View>
                <View style={styles.detalleRow}>
                  <Text style={styles.detalleLabel}>Aprobada:</Text>
                  <Text style={[styles.detalleValue, { color: '#17A2B8' }]}>L. {parseFloat(item.total_aprobada || 0).toFixed(2)}</Text>
                </View>
                <View style={styles.detalleRow}>
                  <Text style={styles.detalleLabel}>Pagada:</Text>
                  <Text style={[styles.detalleValue, { color: '#28A745' }]}>L. {parseFloat(item.total_pagada || 0).toFixed(2)}</Text>
                </View>
              </Card.Content>
            </Card>
          ))}
          {reporte.length === 0 && (
            <Text style={styles.emptyText}>No hay datos para este período</Text>
          )}
        </>
      )}

      <View style={{ height: 20 }} />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { padding: 40, alignItems: 'center' },
  filtersContainer: { padding: 8 },
  chip: { marginRight: 6, backgroundColor: '#f0f0f0' },
  chipActive: { backgroundColor: '#4CAF50' },
  chipTextInactive: { color: '#333' },
  chipTextActive: { color: 'white', fontWeight: '600' },
  card: { margin: 8, elevation: 2 },
  resumenGrid: { flexDirection: 'row', justifyContent: 'space-around' },
  resumenItem: { alignItems: 'center', padding: 8 },
  resumenValue: { fontSize: 16, fontWeight: 'bold' },
  resumenLabel: { fontSize: 12, color: '#666', marginTop: 2 },
  vendedorHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  vendedorNombre: { fontSize: 16, fontWeight: 'bold', color: '#333' },
  vendedorTotal: { fontSize: 18, fontWeight: 'bold', color: '#28A745' },
  divider: { marginVertical: 8 },
  detalleRow: { flexDirection: 'row', justifyContent: 'space-between', marginVertical: 2 },
  detalleLabel: { fontSize: 14, color: '#666' },
  detalleValue: { fontSize: 14, fontWeight: '600', color: '#333' },
  emptyText: { textAlign: 'center', color: '#999', fontSize: 16, marginTop: 40 },
});

export default ComisionReporteScreen;
